import * as React from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { WeeklyReportsSubNav } from "@/components/weekly_reports/WeeklyReportsSubNav"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { MultiSelect } from "@/components/ui/multi-select"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import { useBlockAutoReload } from "@/hooks/use-block-auto-reload"
import type { PageProps } from "@/types/inertia"
import type {
  LmCategory,
  ReportQuestion,
  WeekOption,
  WeeklyReportDetail,
} from "@/types/weekly_report"

const LM_TOTAL = 100
const DESCRIPTION =
  "File your IUPAT weekly activity report: the week's contacts, work, meetings and campaigns, and how your time split across the five LM categories."

export default function WeeklyReportForm({
  report,
  weeks,
  filed_weeks,
  district_councils,
  questions,
  lm_categories,
}: {
  report: WeeklyReportDetail
  weeks: WeekOption[]
  filed_weeks: Record<string, number>
  district_councils: { id: number; name: string }[]
  questions: ReportQuestion[]
  lm_categories: LmCategory[]
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const editing = report.id !== null

  const { data, setData, transform, post, patch, processing, isDirty } = useForm({
    week_ending: report.week_ending ?? weeks[0]?.value ?? "",
    worked_in_organization_ids: report.worked_in_organization_ids,
    // Grouped rather than spread flat so the nine answers and the five
    // percentages stay indexable by key without fighting useForm's typing. They
    // are flattened back out in transform, which is the shape Rails expects.
    answers: report.answers,
    percentages: Object.fromEntries(
      Object.entries(report.percentages).map(([key, value]) => [key, String(value)]),
    ) as Record<string, string>,
  })

  useBlockAutoReload(isDirty)

  const total = lm_categories.reduce(
    (sum, category) => sum + toPercent(data.percentages[category.key]),
    0,
  )
  const totalIsRight = total === LM_TOTAL

  // A week this person has already filed. On an untouched form we just open it;
  // once there is unsaved work we offer the link instead, so nothing typed is
  // thrown away by a stray change of the week picker.
  const clash = editing ? null : (filed_weeks[data.week_ending] ?? null)

  const chooseWeek = (value: string) => {
    const existing = filed_weeks[value]
    if (existing && !isDirty) {
      router.get(`/weekly_reports/${existing}/edit`)
      return
    }
    setData("week_ending", value)
  }

  // The MultiSelect speaks in strings; organization ids are numbers.
  const councilOptions = React.useMemo(
    () => district_councils.map((council) => ({
      value: String(council.id),
      label: council.name,
    })),
    [district_councils],
  )

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!totalIsRight || clash) return

    // Nest explicitly. Rails' ParamsWrapper keeps only keys that are real
    // database columns, so a flat post drops worked_in_organization_ids.
    transform((form) => ({
      weekly_report: {
        week_ending: form.week_ending,
        worked_in_organization_ids: form.worked_in_organization_ids,
        ...form.answers,
        ...form.percentages,
      },
    }))
    if (editing) {
      patch(`/weekly_reports/${report.id}`)
    } else {
      post("/weekly_reports")
    }
  }

  const title = editing ? "Edit weekly report" : "Weekly report"

  return (
    <>
      <Head title={title}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="Weekly Reports"
          description="Your week in your own words, and how the time split across the five LM categories."
          tabs={<WeeklyReportsSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          <form onSubmit={submit} className="space-y-10">
            <section className="space-y-4">
              <div>
                <h2>The week</h2>
                <p className="mt-1 text-sm text-ink-muted">
                  Weeks run Monday to Sunday and are named by the Sunday they end
                  on.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="week_ending">Week</label>
                {editing ? (
                  <p>{report.week_label}</p>
                ) : (
                  <Select
                    id="week_ending"
                    wrapperClassName="max-w-sm"
                    value={data.week_ending}
                    onChange={(event) => chooseWeek(event.target.value)}
                    aria-invalid={Boolean(errors.week_ending)}
                  >
                    {weeks.map((week) => (
                      <option key={week.value} value={week.value}>
                        {week.label}
                        {filed_weeks[week.value] ? " — already filed" : ""}
                      </option>
                    ))}
                  </Select>
                )}
                {errors.week_ending && (
                  <p className="text-xs text-danger-display">{errors.week_ending}</p>
                )}
              </div>

              {clash && (
                <div className="callout callout-signal" role="status">
                  <p className="text-sm">
                    You have already filed a report for this week. Open it to make
                    changes rather than filing a second one.
                  </p>
                  <div className="mt-3">
                    <Button variant="secondary" size="sm" asChild>
                      <Link href={`/weekly_reports/${clash}/edit`}>
                        Open that report
                      </Link>
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="worked_in">Worked in</label>
                <MultiSelect
                  id="worked_in"
                  className="max-w-sm"
                  options={councilOptions}
                  values={data.worked_in_organization_ids.map(String)}
                  onChange={(values) =>
                    setData("worked_in_organization_ids", values.map(Number))
                  }
                  placeholder="Choose District Councils…"
                  searchPlaceholder="Type to filter councils…"
                  emptyMessage="No District Council matches that."
                  selectionLabel="Chosen District Councils"
                />
                <p className="text-xs text-ink-muted">
                  Which District Councils the work touched. Leave it empty if none
                  applies.
                </p>
              </div>
            </section>

            <section className="space-y-4">
              <div>
                <h2>Your week</h2>
                <p className="mt-1 text-sm text-ink-muted">
                  Answer what applies. A quiet week under one of these headings is a
                  real answer — leave it blank.
                </p>
              </div>

              {questions.map((question) => (
                <div key={question.key} className="space-y-2">
                  <label htmlFor={question.key}>{question.label}</label>
                  <Textarea
                    id={question.key}
                    rows={4}
                    value={data.answers[question.key] ?? ""}
                    onChange={(event) =>
                      setData("answers", {
                        ...data.answers,
                        [question.key]: event.target.value,
                      })
                    }
                    aria-invalid={Boolean(errors[question.key])}
                  />
                  {errors[question.key] ? (
                    <p className="text-xs text-danger-display">
                      {errors[question.key]}
                    </p>
                  ) : (
                    question.hint && (
                      <p className="text-xs text-ink-muted">{question.hint}</p>
                    )
                  )}
                </div>
              ))}
            </section>

            <section className="space-y-4">
              <div>
                <h2>LM categories</h2>
                <p className="mt-1 text-sm text-ink-muted">
                  How the week split across the five categories. They have to add up
                  to exactly {LM_TOTAL}%.
                </p>
              </div>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Category</TableHeaderCell>
                    <TableHeaderCell numeric>Percent</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lm_categories.map((category) => (
                    <TableRow key={category.key}>
                      <TableCell>
                        <label htmlFor={category.key} className="font-normal">
                          {category.label}
                        </label>
                      </TableCell>
                      <TableCell numeric>
                        <Input
                          id={category.key}
                          type="number"
                          min={0}
                          max={LM_TOTAL}
                          inputMode="numeric"
                          className="ml-auto w-24 text-right"
                          value={data.percentages[category.key] ?? "0"}
                          onChange={(event) =>
                            setData("percentages", {
                              ...data.percentages,
                              [category.key]: event.target.value,
                            })
                          }
                          aria-invalid={Boolean(errors[category.key]) || !totalIsRight}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell numeric>
                      <span
                        aria-live="polite"
                        className={
                          totalIsRight
                            ? "text-sm text-ink-display"
                            : "text-sm text-danger-display"
                        }
                      >
                        {total}%
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {errors.base && <p className="text-xs text-danger-display">{errors.base}</p>}
              {!totalIsRight && !errors.base && (
                <p className="text-xs text-danger-display">
                  {total > LM_TOTAL
                    ? `That is ${total - LM_TOTAL}% too much.`
                    : `That leaves ${LM_TOTAL - total}% unaccounted for.`}
                </p>
              )}
            </section>

            <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
              <Button
                type="submit"
                disabled={processing || !totalIsRight || Boolean(clash)}
              >
                {processing ? "Saving…" : editing ? "Save changes" : "File this report"}
              </Button>
              <Button variant="ghost" asChild>
                <Link href="/weekly_reports">Cancel</Link>
              </Button>
              {editing && report.updated_at && (
                <span className="text-xs text-ink-muted">
                  Last updated {formatStamp(report.updated_at)}
                </span>
              )}
            </div>
          </form>
        </div>
      </AppShell>
    </>
  )
}

function toPercent(value: unknown) {
  const parsed = Number.parseInt(String(value ?? ""), 10)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatStamp(value: string) {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}
